import { Card, CardContent, Grid, Typography, TableContainer, Table, TableHead, TableCell, TableRow, TableBody, Paper, makeStyles, Box, Chip } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import React from 'react'
import { useSelector } from 'react-redux'

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    maxHeight: 600,
    boxShadow: 'none',
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[200],
    fontWeight: 600,
    color: theme.palette.text.secondary,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  nameCell: {
    fontWeight: 500,
    minWidth: 180,
  },
  indexCell: {
    width: 50,
    color: theme.palette.text.secondary,
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(3),
  },
  chipHighQuota: {
    backgroundColor: '#398E3C',
    color: '#ffffff',
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  chipHighPotential: {
    backgroundColor: '#C9E7C8',
    color: '#2e7d32',
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  chipPipelineBurner: {
    backgroundColor: '#FEE1B2',
    color: '#b45309',
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  legendContainer: {
    marginTop: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  legendText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
}))
const AnnotationTable = () => {
  const classes = useStyles()
  const annotation = useSelector(state => state.app.repPerformanceAnnotations)
  const annotationMeta = annotation?.meta || {}

  const getColumnHeader = (key) => {
    return annotationMeta[key]?.label || key
  }

  const getChipClass = (key) => {
    const classMap = {
      'HIGH_QUOTA_ATTAINER': classes.chipHighQuota,
      'HIGH_POTENTIAL_REP': classes.chipHighPotential,
      'PIPELINE_BURNER': classes.chipPipelineBurner
    }
    return classMap[key] || ''
  }

  const getChipLabel = (key) => {
    return annotationMeta[key]?.label || key
  }

  const defaultLegendItems = [
    {
      key: 'HIGH_QUOTA_ATTAINER',
      description: 'Consistently exceeds quota expectations. Represents top performance through strong execution and deal closure.'
    },
    {
      key: 'HIGH_POTENTIAL_REP',
      description: 'Demonstrates strong conversion effectiveness with limited pipeline pursued. Increasing pipeline access can unlock additional Closed-Won impact.'
    },
    {
      key: 'PIPELINE_BURNER',
      description: 'Shows high pipeline consumption with lower conversion efficiency. Targeted coaching and deal-quality improvements can enhance outcomes.'
    }
  ]
  const legendItems = defaultLegendItems.map((item) => ({
    ...item,
    label: annotationMeta[item.key]?.label || item.key,
    description: item.description,
  }))

  const hasAnnotationData = annotation && annotation['x-keys'] && annotation['y-keys']

  return (
    <Grid item xs={12} style={{ margin: "0.5rem" }}>
        <Card style={{ margin: "auto", width: "98%" }} raised={true}>
          <CardContent>
            <Typography variant="h6" align='center' className={classes.title}>
              Rep Performance Classification
            </Typography>
            {hasAnnotationData ? (
              <>
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={`${classes.tableHeader} ${classes.indexCell}`}>
                          #
                        </TableCell>
                        <TableCell className={classes.tableHeader}>
                          Rep Name
                        </TableCell>
                        {annotation['x-keys'].map((key) => (
                          <TableCell key={key} align="center" className={classes.tableHeader}>
                            {getColumnHeader(key)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {annotation['y-keys'].map((repName, index) => {
                        const subjectKey = `subject-${repName}`
                        const repData = annotation[subjectKey]

                        return (
                          <TableRow key={repName} hover>
                            <TableCell className={classes.indexCell}>
                              {index + 1}
                            </TableCell>
                            <TableCell className={classes.nameCell}>
                              {repName}
                            </TableCell>
                            {annotation['x-keys'].map((category) => (
                              <TableCell key={category} align="center">
                                {repData && repData[category] ? (
                                  <Chip
                                    label={getChipLabel(category)}
                                    className={getChipClass(category)}
                                    size="small"
                                  />
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box className={classes.legendContainer}>
                  {legendItems.map((item) => (
                    <Box key={item.key} className={classes.legendItem}>
                      <Chip
                        label={item.label}
                        className={getChipClass(item.key)}
                        size="small"
                      />
                      <Typography className={classes.legendText}>
                        {item.description}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={`${classes.tableHeader} ${classes.indexCell}`}>
                        <Skeleton width={20} />
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        <Skeleton width={80} />
                      </TableCell>
                      <TableCell align="center" className={classes.tableHeader}>
                        <Skeleton width={120} />
                      </TableCell>
                      <TableCell align="center" className={classes.tableHeader}>
                        <Skeleton width={120} />
                      </TableCell>
                      <TableCell align="center" className={classes.tableHeader}>
                        <Skeleton width={100} />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(8)].map((_, index) => (
                      <TableRow key={index}>
                        <TableCell className={classes.indexCell}>
                          <Skeleton width={20} />
                        </TableCell>
                        <TableCell className={classes.nameCell}>
                          <Skeleton width={150} />
                        </TableCell>
                        <TableCell align="center">
                          <Skeleton variant="rect" width={120} height={24} style={{ borderRadius: 16 }} />
                        </TableCell>
                        <TableCell align="center">
                          <Skeleton variant="rect" width={120} height={24} style={{ borderRadius: 16 }} />
                        </TableCell>
                        <TableCell align="center">
                          <Skeleton variant="rect" width={100} height={24} style={{ borderRadius: 16 }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
    </Grid>
  )
}

export default AnnotationTable
